import { skipToken } from "@reduxjs/toolkit/query/react"
import { useGetMeQuery, useGetUsersQuery } from "./api"
import { getTokenAvailable } from "@/auth"
import { useSelector } from "@/base"
import type { Uuid } from "@/utils"

type MeOptions = {
  readonly skip?: boolean
  readonly pollingInterval?: number
}

/**
 * Reads the signed-in user.
 *
 * The skip is an optimisation, not a correctness guard. The access token
 * isn't persisted, so each boot renders without one until AuthWrapper's refresh
 * effect, which runs after its children's, completes; skipping spares those
 * renders a request that would 401 and
 * retry behind the refresh mutex. An early request is still safe:
 * `/api/users/me` is authenticated, so it 401s and baseApi refreshes and
 * retries.
 */
export const useMe = ({ skip, pollingInterval }: MeOptions = {}) => {
  const isAuthenticated = useSelector(getTokenAvailable)

  return useGetMeQuery(undefined, {
    skip: !isAuthenticated || skip,
    pollingInterval,
  })
}

export const useGetUser = (id?: Uuid) =>
  useGetUsersQuery(
    id
      ? {
          ids: [id],
        }
      : skipToken,
    {
      selectFromResult: ({ data, isLoading, isUninitialized }) => ({
        user: data?.[0],
        isLoading: isLoading || isUninitialized,
      }),
    },
  )
